import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import toast from "react-hot-toast";
import { API } from "../../../api";

const fieldSx = {
  flex: "1 1 140px",
  minWidth: 130,
  "& .MuiInputBase-root": {
    fontSize: 13,
    height: 34,
    background: "#fff",
  },
  "& .MuiOutlinedInput-input": {
    py: 0.5,
    px: 1,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d0e4f7",
  },
  "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#126ABC",
  },
  "& .MuiFormHelperText-root": {
    margin: 0,
    fontSize: 11,
  },
};

function buildFormState(row) {
  return {
    tracking_number: row?.raw_tracking_number ?? "",
    carrier: row?.raw_carrier ?? "",
    tracking_url: row?.raw_tracking_url ?? "",
  };
}

function isValidTrackingUrl(url) {
  const trimmed = url.trim();
  if (!trimmed) return true;
  try {
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(normalized);
    return Boolean(parsed.hostname);
  } catch {
    return false;
  }
}

function validateFields(values) {
  const errors = {};

  if (values.tracking_number.length > 100) {
    errors.tracking_number = "Max 100 characters";
  }
  if (values.carrier.length > 100) {
    errors.carrier = "Max 100 characters";
  }
  if (values.tracking_url.length > 500) {
    errors.tracking_url = "Max 500 characters";
  } else if (!isValidTrackingUrl(values.tracking_url)) {
    errors.tracking_url = "Enter a valid URL";
  }

  return errors;
}

export default function OrderTrackingEditableFields({ row, recordList }) {
  const [values, setValues] = useState(() => buildFormState(row));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(buildFormState(row));
    setErrors({});
  }, [
    row?.id,
    row?.raw_tracking_number,
    row?.raw_carrier,
    row?.raw_tracking_url,
  ]);

  const savedValues = buildFormState(row);
  const isDirty =
    values.tracking_number.trim() !== String(savedValues.tracking_number).trim() ||
    values.carrier.trim() !== String(savedValues.carrier).trim() ||
    values.tracking_url.trim() !== String(savedValues.tracking_url).trim();

  const handleChange = (field) => (event) => {
    const next = event.target.value;
    setValues((prev) => ({ ...prev, [field]: next }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSave = async () => {
    const payload = {
      tracking_number: values.tracking_number.trim(),
      carrier: values.carrier.trim(),
      tracking_url: values.tracking_url.trim(),
    };

    const validationErrors = validateFields(payload);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the highlighted fields");
      return;
    }

    if (!isDirty) return;

    try {
      setSaving(true);
      const response = await API.adminOrderTrackingUpdate(row.id, payload);
      toast.success(response?.data?.message || "Tracking info saved");
      recordList?.();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to save tracking info");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && isDirty && !saving) {
      event.preventDefault();
      handleSave();
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
      <TextField
        size="small"
        value={values.tracking_number}
        onChange={handleChange("tracking_number")}
        onKeyDown={handleKeyDown}
        disabled={saving}
        placeholder="Tracking #"
        error={Boolean(errors.tracking_number)}
        helperText={errors.tracking_number}
        sx={fieldSx}
      />
      <TextField
        size="small"
        value={values.carrier}
        onChange={handleChange("carrier")}
        onKeyDown={handleKeyDown}
        disabled={saving}
        placeholder="Carrier"
        error={Boolean(errors.carrier)}
        helperText={errors.carrier}
        sx={fieldSx}
      />
      <TextField
        size="small"
        value={values.tracking_url}
        onChange={handleChange("tracking_url")}
        onKeyDown={handleKeyDown}
        disabled={saving}
        placeholder="Tracking URL"
        error={Boolean(errors.tracking_url)}
        helperText={errors.tracking_url}
        sx={{ ...fieldSx, flex: "1 1 180px", minWidth: 160 }}
      />
      <Button
        size="small"
        variant="contained"
        onClick={handleSave}
        disabled={!isDirty || saving}
        sx={{
          minWidth: 64,
          height: 34,
          mt: errors.tracking_number || errors.carrier || errors.tracking_url ? "18px" : 0,
          px: 1.5,
          fontSize: 12,
          fontWeight: 600,
          textTransform: "none",
          flexShrink: 0,
          background: "#126ABC",
          boxShadow: "none",
          "&:hover": { background: "#0f5a9e", boxShadow: "none" },
          "&.Mui-disabled": { background: "#9BBEE4", color: "#fff" },
        }}
      >
        {saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : "Save"}
      </Button>
    </Box>
  );
}
